import { CloseCode, type ClientMessage, type ServerMessage } from '../../../shared/protocol';

export type ConnectionState = 'connecting' | 'online' | 'offline' | 'unauthorized' | 'forbidden' | 'replaced';

const RETRY_MIN_MS = 1000;
const RETRY_MAX_MS = 15000;

/**
 * Conexão com o servidor do jogo (/ws na mesma origem), com reconexão
 * automática. O `hello` é montado a cada conexão.
 */
export class GameSocket {
  private ws: WebSocket | null = null;
  private retryMs = RETRY_MIN_MS;
  private retryTimer: number | undefined;
  private messageListeners: ((message: ServerMessage) => void)[] = [];
  private stateListeners: ((state: ConnectionState) => void)[] = [];
  private currentState: ConnectionState = 'offline';

  constructor(private readonly hello: () => ClientMessage) {}

  get state(): ConnectionState {
    return this.currentState;
  }

  onMessage(listener: (message: ServerMessage) => void): void {
    this.messageListeners.push(listener);
  }

  onState(listener: (state: ConnectionState) => void): void {
    this.stateListeners.push(listener);
  }

  connect(): void {
    window.clearTimeout(this.retryTimer);
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;

    this.setState('connecting');
    // Relativo à página: funciona tanto na raiz do domínio quanto num subcaminho.
    const url = new URL('ws', location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.addEventListener('open', () => ws.send(JSON.stringify(this.hello())));
    ws.addEventListener('message', (event) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }
      if (message.t === 'welcome') {
        this.retryMs = RETRY_MIN_MS;
        this.setState('online');
      }
      this.messageListeners.forEach((listener) => listener(message));
    });
    ws.addEventListener('close', (event) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (event.code === CloseCode.Unauthorized) return this.setState('unauthorized');
      if (event.code === CloseCode.NotAllowed) return this.setState('forbidden');
      if (event.code === CloseCode.Replaced) return this.setState('replaced');
      this.setState('offline');
      this.retryTimer = window.setTimeout(() => this.connect(), this.retryMs);
      this.retryMs = Math.min(this.retryMs * 2, RETRY_MAX_MS);
    });
  }

  /** Celular costuma derrubar a conexão em segundo plano: reconecta ao voltar. */
  reconnectNow(): void {
    if (this.currentState === 'offline') {
      this.retryMs = RETRY_MIN_MS;
      this.connect();
    }
  }

  send(message: ClientMessage): boolean {
    if (this.currentState !== 'online' || this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(message));
    return true;
  }

  private setState(state: ConnectionState): void {
    if (state === this.currentState) return;
    this.currentState = state;
    this.stateListeners.forEach((listener) => listener(state));
  }
}
